
import type { Session, User } from '@supabase/supabase-js';

/**
 * Core permissions state representing all user authorization levels
 * This serves as the single source of truth for permission status
 */
export interface PermissionsState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the user has premium subscription access */
  isPremium: boolean;
  /** Whether the user has admin privileges */
  isAdmin: boolean;
  /** Current status of permission checks */
  permissionsStatus: 'loading' | 'ready' | 'error';
  /** Timestamp of last successful permission check */
  lastChecked: number;
  /** Source of subscription status for debugging */
  source?: 'stripe' | 'admin_override' | 'fallback';
  /** Error message if permission check failed */
  error?: string;
}

/**
 * Cached permissions data with expiration and versioning
 * Used for offline fallback and performance optimization
 */
export interface PermissionsCache {
  /** The cached permissions state */
  permissions: PermissionsState;
  /** Timestamp when this cache expires */
  expiresAt: number;
  /** Cache version for invalidation during schema changes */
  version: string;
}

/**
 * Complete permissions context interface
 * Extends PermissionsState with control methods
 */
export interface PermissionsContextType extends PermissionsState {
  /** Force refresh permissions from server */
  refreshPermissions: () => Promise<void>;
  /** Clear cached permissions data */
  clearCache: () => void;
  /** Whether permissions are currently being refreshed */
  isRefreshing: boolean;
  /** User object for backward compatibility */
  user?: User;
  /** Session object for backward compatibility */
  session?: Session;
  /** Legacy loading state for backward compatibility */
  isLoading: boolean;
  /** Legacy authReady state for backward compatibility */
  authReady: boolean;
}

/**
 * Configuration options for permissions system
 */
export interface PermissionsConfig {
  /** Cache TTL in milliseconds (default: 5 minutes) */
  cacheTTL?: number;
  /** Whether to use offline fallback (default: true) */
  enableOfflineFallback?: boolean;
  /** Cache version for invalidation */
  cacheVersion?: string;
}

/**
 * Permission check result from API
 */
export interface PermissionCheckResult {
  isAuthenticated: boolean;
  isPremium: boolean;
  isAdmin: boolean;
  source?: 'stripe' | 'admin_override' | 'fallback';
  timestamp: number;
}

/**
 * Events that can trigger permission refresh
 */
export type PermissionRefreshTrigger = 
  | 'login'
  | 'logout' 
  | 'subscription_change'
  | 'admin_change'
  | 'manual'
  | 'cache_expired'
  | 'auth_ready_user_present'
  | 'permissions_timeout'
  | 'signed_in';

/**
 * Default permissions state for unauthenticated users
 */
export const DEFAULT_PERMISSIONS: PermissionsState = {
  isAuthenticated: false,
  isPremium: false,
  isAdmin: false,
  permissionsStatus: 'ready',
  lastChecked: 0,
};

/**
 * Default configuration values
 */
export const DEFAULT_PERMISSIONS_CONFIG: Required<PermissionsConfig> = {
  cacheTTL: 5 * 60 * 1000, // 5 minutes
  enableOfflineFallback: true,
  cacheVersion: '1.0.0',
};
